import { Clock } from 'lucide-react-native';
import { useEffect, useRef } from 'react';

import { SupportForm } from '@/components/supportForm';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useSupportPrompt } from '@/features/support-contact';
import { describeReadyAt, useRetryAt } from '@/hooks/useRetryAt';
import { getRateLimitDelay } from '@/util/rateLimit';

/** What the member asks support for, whether they reach it by thread or by form. */
const MORE_ACCESS_REQUEST =
  'I’d like to browse more of the Inquiry Library than the current limit allows.';

type LibraryRetryNoticeProps = {
  error: unknown;
  /**
   * When the error arrived, as the query's `errorUpdatedAt`. The server's wait
   * is counted from here, so a notice for an old error knows what is left.
   */
  failedAt: number;
  onRetry: () => void;
  /** Leaves for the Inquiry Library list. Offered on an inquiry, where the list still works. */
  onBack?: () => void;
  /** Whether the copy should talk about one post or the list. */
  surface: 'post' | 'list';
  /**
   * `inline` is a one-line strip above content that is still on screen, for a
   * refresh that failed after the first load succeeded. `full` stands in for
   * content that never loaded.
   */
  variant?: 'full' | 'inline';
};

/**
 * What a reader sees when the library refuses to load.
 *
 * Two failures reach this: the API is over its rate limit, which clears on its
 * own, and everything else, which does not. The rate-limit copy names it
 * plainly as a security limit rather than describing the reader's own
 * browsing, and offers Contact Support for anyone who needs a higher one.
 *
 * **The wait is a fixed moment, not a countdown.** "At 10:42 AM" never changes
 * while the reader waits, so there is no auto-updating text for WCAG 2.2.2 to
 * require a pause control on, and the polite live region announces the notice
 * once instead of every second.
 *
 * **It retries by itself.** When the server's wait is over the notice retries
 * once, so a reader who stays put gets the post without pressing anything. Try
 * again appears only when there is nothing left to wait for, rather than as a
 * disabled button for minutes. Meanwhile the only action is Back to Inquiry Library,
 * since inquiries the reader has already opened load from cache.
 */
export function LibraryRetryNotice({
  error,
  failedAt,
  onRetry,
  onBack,
  surface,
  variant = 'full',
}: LibraryRetryNoticeProps) {
  const supportPrompt = useSupportPrompt({
    source: 'library_retry',
    draft: MORE_ACCESS_REQUEST,
  });

  const { isRateLimited, seconds } = getRateLimitDelay(error);

  const { readyAt, isReady } = useRetryAt(isRateLimited ? seconds : undefined, failedAt);

  const isWaiting = isRateLimited && !isReady;

  useRetryOnceWhenReady({
    shouldRetry: isRateLimited && readyAt !== undefined,
    isReady,
    failedAt,
    onRetry,
  });

  /**
   * Without the server's `Retry-After` there is no moment to name and nothing
   * to retry at, so the copy promises no automatic load and Try again stays up.
   */
  const knowsWhen = readyAt !== undefined;

  const when = knowsWhen ? describeReadyAt(readyAt) : '';

  if (variant === 'inline') {
    const inlineMessage =
      isWaiting && knowsWhen
        ? `Couldn’t refresh. Showing what loaded earlier. Tries again ${when}.`
        : 'Couldn’t refresh. Showing what loaded earlier.';

    return (
      <HStack
        className="mx-5 mt-4 items-center justify-between gap-3 rounded-lg bg-background-muted px-4 py-3"
        accessibilityLiveRegion="polite"
        testID="library-retry-notice-inline">
        <Text size="sm" className="flex-1 text-typography-700">
          {inlineMessage}
        </Text>

        {isWaiting ? null : (
          <Button size="sm" variant="link" action="primary" onPress={onRetry}>
            <ButtonText>Try again</ButtonText>
          </Button>
        )}
      </HStack>
    );
  }

  const subject = surface === 'post' ? 'This inquiry' : 'The Inquiry Library';

  const title = isRateLimited ? 'You’ve hit our security limit' : 'This didn’t load';

  const rateLimitMessage = knowsWhen
    ? `${subject} will load again ${when}.`
    : 'Give it a moment, then try again.';

  const alreadyOpened = surface === 'post' ? ' Inquiries you’ve already opened still work.' : '';

  const message = isRateLimited
    ? `${rateLimitMessage}${alreadyOpened}`
    : 'Check your connection and try again.';

  return (
    <VStack
      className="w-full flex-1 items-center justify-center gap-3 px-6 pb-24 pt-10 web:min-h-[60vh]"
      accessibilityLiveRegion="polite"
      testID="library-retry-notice">
      {isRateLimited ? (
        <Icon as={Clock} size="xl" className="text-typography-700" aria-hidden />
      ) : null}

      <Text className="text-center font-heading text-lg text-typography-900">{title}</Text>

      <Text className="max-w-md text-center text-base text-typography-700">{message}</Text>

      {isRateLimited ? (
        <Button size="sm" variant="link" action="primary" onPress={supportPrompt.open}>
          <ButtonText>Need more? Contact support.</ButtonText>
        </Button>
      ) : null}

      <HStack className="flex-wrap justify-center gap-3">
        {isWaiting ? null : (
          <Button size="sm" action="primary" onPress={onRetry}>
            <ButtonText>Try again</ButtonText>
          </Button>
        )}

        {onBack ? <BackToLibraryButton onPress={onBack} isOnlyAction={isWaiting} /> : null}
      </HStack>

      <SupportForm
        isOpen={supportPrompt.isFormOpen}
        onClose={supportPrompt.closeForm}
        initialValues={{
          reason: 'Something Else',
          message: MORE_ACCESS_REQUEST,
        }}
      />
    </VStack>
  );
}

/**
 * The way out while the post waits.
 *
 * Solid when it is the only action on screen, so it cannot read as disabled.
 * Beside Try again it steps down to an outline so the screen keeps one primary
 * action. The outline's own gray text fails AA on white, so it takes the green
 * shades that pass in each theme instead.
 */
function BackToLibraryButton({
  onPress,
  isOnlyAction,
}: {
  onPress: () => void;
  isOnlyAction: boolean;
}) {
  if (isOnlyAction) {
    return (
      <Button size="sm" action="primary" onPress={onPress}>
        <ButtonText>Back to Inquiry Library</ButtonText>
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      action="primary"
      className="border-primary-500"
      onPress={onPress}>
      <ButtonText className="text-primary-600 dark:text-primary-700">
        Back to Inquiry Library
      </ButtonText>
    </Button>
  );
}

/**
 * Retries once per refusal when its wait ends. Keyed on `failedAt`, so a second
 * refusal earns one more try and a re-render never fires a second request for
 * the same one.
 */
function useRetryOnceWhenReady({
  shouldRetry,
  isReady,
  failedAt,
  onRetry,
}: {
  shouldRetry: boolean;
  isReady: boolean;
  failedAt: number;
  onRetry: () => void;
}) {
  const retriedFor = useRef<number | null>(null);

  useEffect(() => {
    if (!shouldRetry || !isReady || retriedFor.current === failedAt) {
      return;
    }

    retriedFor.current = failedAt;

    onRetry();
  }, [shouldRetry, isReady, failedAt, onRetry]);
}
